import axios from 'axios';
import { API_BASE } from './theme';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Auto-attach token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('client_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Redirect on 401
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('client_token');
      localStorage.removeItem('client_user');
      window.location.hash = '#/login';
    }
    return Promise.reject(err);
  }
);

// --- AUTH ---
export const auth = {
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  register: (data: any) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  updateMe: (data: any) => api.put('/auth/me', data),
  completeProfile: (data: any) => api.put('/auth/complete-profile', data),
  changePassword: (old_password: string, new_password: string) =>
    api.post('/auth/change-password', { old_password, new_password }),
  uploadPhoto: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/auth/upload-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  loyalty: () => api.get('/auth/loyalty'),
};

// --- PRODUCTS ---
export const products = {
  list: (params?: any) => api.get('/products/', { params }),
  categories: () => api.get('/products/categories'),
  menu: () => api.get('/products/menu'),
  cateringMenu: () => api.get('/products/catering-menu'),
  get: (id: number) => api.get(`/products/${id}`),
};

// --- RESERVATIONS ---
export const reservations = {
  packages: () => api.get('/reservations/packages'),
  tables: (params?: any) => api.get('/reservations/tables', { params }),
  create: (data: any) => api.post('/reservations/', data),
  my: () => api.get('/reservations/my'),
  myLastBill: () => api.get('/reservations/my-last-bill'),
  get: (id: number) => api.get(`/reservations/${id}`),
  checkIn: (reservation_id?: number) => api.post('/reservations/check-in', { reservation_id }),
  checkOut: () => api.post('/reservations/check-out'),
  createOrder: (data: any) => api.post('/reservations/orders', data),
};

// --- SOCIAL ---
export const social = {
  toggleMode: () => api.post('/social/toggle-mode'),
  activeUsers: () => api.get('/social/active-users'),
  invite: (data: any) => api.post('/social/invite', data),
  receivedInvitations: () => api.get('/social/invitations/received'),
  sentInvitations: () => api.get('/social/invitations/sent'),
  respondInvitation: (id: number, status: string) =>
    api.post(`/social/invitations/${id}/respond`, { status }),
  sendChat: (invitationId: number, content: string) =>
    api.post(`/social/chat/${invitationId}`, { content }),
  getChat: (invitationId: number) => api.get(`/social/chat/${invitationId}`),
};

// --- COMMUNITY ---
export const community = {
  feed: (params?: any) => api.get('/community/feed', { params }),
  createPost: (data: any) => api.post('/community/posts', data),
  likePost: (id: number) => api.post(`/community/posts/${id}/like`),
  deletePost: (id: number) => api.delete(`/community/posts/${id}`),
  contests: (active_only?: boolean) => api.get('/community/contests', { params: { active_only } }),
  submitContestEntry: (contestId: number, postId: number) =>
    api.post(`/community/contests/${contestId}/entries/${postId}`),
  events: (upcoming?: boolean) => api.get('/community/events', { params: { upcoming } }),
  registerEvent: (id: number) => api.post(`/community/events/${id}/register`),
  cancelEvent: (id: number) => api.post(`/community/events/${id}/cancel`),
  notifications: () => api.get('/community/notifications'),
  markRead: (id: number) => api.post(`/community/notifications/${id}/read`),
  markAllRead: () => api.post('/community/notifications/read-all'),
};

export default api;